/*
 * app-lazy-loader.js
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * Shopify injects App-embed entry scripts into `content_for_header`, which is
 * a single Liquid tag and therefore cannot be wrapped in `{% if template %}`.
 * Every page pays for every enabled app, above the fold, no matter what.
 *
 * This loader flips that around: the snippet `app-lazy-loader.liquid` declares
 * the entry-script URLs as data attributes, and this file injects them only
 * when they are actually needed. Pair it with disabling the matching App embed
 * in the theme editor — otherwise the app is loaded twice.
 *
 * CONTRACT
 *   <div hidden data-app-lazy="interaction" data-app-consent="marketing" data-app-src="URL"></div>
 *     -> injected on the first real user input (pointer/key/touch/wheel/scroll)
 *   <div hidden data-app-lazy="defer" data-app-consent="marketing" data-app-src="URL"></div>
 *     -> injected once the browser is idle (3s ceiling)
 *   data-app-consent names the Shopify Customer Privacy category the script
 *   belongs to: "marketing" | "analytics" | "preferences". Omitted -> marketing.
 *
 * CONSENT (GDPR)
 *   A script is injected only while window.Shopify.customerPrivacy reports its
 *   category as allowed. Until that API resolves we hold back instead of
 *   guessing, and retry both on Shopify's `visitorConsentCollected` event and
 *   with a short bounded poll. If the API never appears, the store has no
 *   consent banner configured, so there is no consent to respect and we keep
 *   the previous behaviour. Requires Shopify admin → Settings → Customer
 *   privacy → cookie banner to be enabled, otherwise there is no API at all.
 *   Caveat: a later consent withdrawal cannot un-load an injected script; a
 *   page reload is required for that.
 *
 * SAFETY
 *   inject() is idempotent: if a <script> with the same URL (query string
 *   ignored) is already in the DOM, nothing happens. That means this file can
 *   ship before the embed is switched off, and it can coexist with the embed
 *   while you roll the change out app by app.
 * ---------------------------------------------------------------------------
 */
(() => {
  const NODES = Array.from(document.querySelectorAll("[data-app-lazy]"));
  if (NODES.length === 0) return;

  const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel", "scroll"];
  const IDLE_TIMEOUT_MS = 3000;

  const DEFAULT_CONSENT = "marketing";
  const CONSENT_METHODS = {
    marketing: "marketingAllowed",
    analytics: "analyticsProcessingAllowed",
    preferences: "preferencesProcessingAllowed",
  };
  const CONSENT_POLL_MS = 250;
  const CONSENT_GIVE_UP_MS = 5000;

  /** True when a script with this URL (ignoring the query string) is already in the DOM. */
  const alreadyLoaded = (src) => {
    const base = src.split("?")[0];
    return !!document.querySelector('script[src^="' + base + '"]');
  };

  const inject = (src) => {
    if (!src || alreadyLoaded(src)) return;
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    document.head.appendChild(el);
  };

  /* ---------- Consent ----------
     Shopify's own banner loads independently of this file, so the API may be
     missing at the moment a trigger fires. Fail closed until it resolves. */

  const privacy = () => (window.Shopify && window.Shopify.customerPrivacy) || null;

  /** true = allowed, false = denied, null = API not ready yet. */
  const consentState = (category) => {
    const cp = privacy();
    if (!cp) return null;
    const method = CONSENT_METHODS[category] || CONSENT_METHODS[DEFAULT_CONSENT];
    if (typeof cp[method] !== "function") return null;
    try {
      return !!cp[method]();
    } catch (error) {
      return null;
    }
  };

  // Set once the API has not appeared within the grace window: no banner is
  // configured, so there is no consent to gate on.
  let noConsentApi = false;
  const consentAllows = (category) => {
    const state = consentState(category);
    return state === null ? noConsentApi : state;
  };

  /* ---------- Triggers ---------- */

  const groups = { defer: [], interaction: [] };
  for (const node of NODES) {
    const name = node.dataset.appLazy === "defer" ? "defer" : "interaction";
    groups[name].push({
      src: node.dataset.appSrc,
      category: node.dataset.appConsent || DEFAULT_CONSENT,
    });
  }

  const reached = { defer: false, interaction: false };

  const flush = (name) => {
    if (!reached[name]) return;
    for (const item of groups[name]) {
      if (consentAllows(item.category)) inject(item.src);
    }
  };

  const flushAll = () => {
    flush("defer");
    flush("interaction");
  };

  if (groups.defer.length > 0) {
    const reachIdle = () => {
      reached.defer = true;
      flush("defer");
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(reachIdle, { timeout: IDLE_TIMEOUT_MS });
    } else {
      window.setTimeout(reachIdle, IDLE_TIMEOUT_MS);
    }
  }

  if (groups.interaction.length > 0) {
    const onFirstInput = () => {
      INTERACTION_EVENTS.forEach((type) =>
        window.removeEventListener(type, onFirstInput, { passive: true })
      );
      reached.interaction = true;
      flush("interaction");
    };
    INTERACTION_EVENTS.forEach((type) =>
      window.addEventListener(type, onFirstInput, { passive: true })
    );
  }

  /* ---------- Wait for the consent API ---------- */

  if (!privacy()) {
    const startedAt = Date.now();
    const waitForApi = () => {
      if (privacy() || Date.now() - startedAt >= CONSENT_GIVE_UP_MS) {
        if (!privacy()) noConsentApi = true;
        flushAll();
        return;
      }
      window.setTimeout(waitForApi, CONSENT_POLL_MS);
    };
    waitForApi();
  }

  // Fires whenever the visitor grants or changes their consent choice.
  document.addEventListener("visitorConsentCollected", flushAll);
})();
